import {
  BroadcastModeUpdate,
  EChannelBroadcastMode,
  IChannelBroadcastModeChecker,
} from "../../domain/broadcasts/i-channel-broadcast-mode-checker";
import { BehaviorSubject, Subject } from "rxjs";
import { Injector, Injectable } from "@angular/core";
import moment from "moment/moment";
import { Socket } from "../socket.service";
import { ShowRepository } from "../../domain/show-repository";
import { ChannelRepository } from "../../domain/channel-repository";
import { UserService } from "../../domain/user-service";
import { User } from "../../domain/user";
import { BroadcastPresetShow, Show } from "../../domain/show";
import { Channel } from "../../domain/channel";
import { CalendarService } from "../../domain/calendar-service";

export interface ChannelBroadcastModeState {
  channelId: number;
  channelName: string;
  mode: EChannelBroadcastMode;
  isActive: boolean;
  liveBroadcastPresetShowUid?: string;
  nextLiveBroadcastPresetShow?: BroadcastPresetShow;
  checkStatusTimeoutReference?: any;
  scheduleUpdateListenerHandler?: any;
}

@Injectable()
export class ChannelBroadcastModeChecker
  implements IChannelBroadcastModeChecker
{
  public modeUpdated = new BehaviorSubject<BroadcastModeUpdate>({} as any);
  public nextLiveBroadcastPresetShow: BroadcastPresetShow;

  // New properties to manage multiple channels
  private channelStates: Map<number, ChannelBroadcastModeState> = new Map();
  private _currentUser: User;
  private _lastGetShowsSubject: Subject<void>;
  // Track the global broadcast mode
  private _globalBroadcastMode: EChannelBroadcastMode =
    EChannelBroadcastMode.Off;
  // Track the currently selected channel ID
  private _currentChannelId: number | null = null;

  /** Resolved lazily to avoid triggering CalendarService constructor (and its onInit-like logic) during bootstrap. */
  private _calendarService: CalendarService | null = null;

  constructor(
    private _socketService: Socket,
    private _showRepository: ShowRepository,
    private _channelRepository: ChannelRepository,
    private _injector: Injector,
    private _userService: UserService,
  ) {}

  private get calendarService(): CalendarService {
    if (this._calendarService == null) {
      this._calendarService = this._injector.get(CalendarService);
    }
    return this._calendarService;
  }

  async init() {
    console.log("ChannelBroadcastModeChecker->init()");
    this._currentUser = this._userService.getCurrentUser();
    var currentChannel = await this._userService.getCurrentChannel();
    if (currentChannel) {
      this._currentChannelId = currentChannel.id;
    }
    // Subscribe to channel changes
    this._channelRepository.channelSaved.subscribe(async () => {
      await this.refreshChannels();
    });

    this._channelRepository.channelDeleted.subscribe(async () => {
      await this.refreshChannels();
    });

    // Subscribe to active channel changes
    this._userService.currentChannelIdUpdated.subscribe(async (channelId) => {
      this._currentChannelId = channelId;
      if (channelId) {
        await this.emitChannelSpecificUpdate(channelId);
      }
    });

    // Initial load of channels
    await this.refreshChannels();

    // Initial update of global broadcast mode
    this.updateGlobalBroadcastMode();

    // If we have a current channel, emit its state too
    if (this._currentChannelId) {
      await this.emitChannelSpecificUpdate(this._currentChannelId);
    }
  }

  private async refreshChannels() {
    try {
      const allChannels = await this._channelRepository.getTenantChannels();
      await this.setupChannels(allChannels);
    } catch (error) {
      console.error("Failed to refresh channels:", error);
    }
  }

  private async setupChannels(channels: Channel[]) {
    console.log(
      `ChannelBroadcastModeChecker->setupChannels(${channels.length} channels)`,
    );

    // Clean up any existing listeners for channels that are no longer in the list
    const currentChannelIds = channels.map((c) => c.id);
    for (const [channelId, state] of this.channelStates.entries()) {
      if (!currentChannelIds.includes(channelId)) {
        if (state.scheduleUpdateListenerHandler) {
          this._socketService.stopListeningForSchedulerUpdates(
            state.scheduleUpdateListenerHandler,
          );
        }
        if (state.checkStatusTimeoutReference) {
          clearTimeout(state.checkStatusTimeoutReference);
        }
        this.channelStates.delete(channelId);
      }
    }

    if (channels.length === 1) {
      this._currentChannelId = channels[0].id;
    }

    // Setup for each active and non-block channel
    for (const channel of channels) {
      if (!channel.isBlock) {
        // If the channel is not in our states map, initialize it
        if (!this.channelStates.has(channel.id)) {
          this.channelStates.set(channel.id, {
            channelId: channel.id,
            channelName: channel.name,
            mode: EChannelBroadcastMode.Off,
            isActive: channel.isActive,
            scheduleUpdateListenerHandler: null,
            checkStatusTimeoutReference: null,
            liveBroadcastPresetShowUid: null,
            nextLiveBroadcastPresetShow: null,
          });
        } else {
          // l name in case it changed
          const state = this.channelStates.get(channel.id);
          state.channelName = channel.name;
          state.isActive = channel.isActive;
          this.channelStates.set(channel.id, state);
        }

        const state = this.channelStates.get(channel.id);

        // Clean up previous listener if exists
        if (state.scheduleUpdateListenerHandler) {
          this._socketService.stopListeningForSchedulerUpdates(
            state.scheduleUpdateListenerHandler,
          );
        }

        // Set up new listener
        state.scheduleUpdateListenerHandler = this.createSchedulerUpdateHandler(
          channel.id,
        );
        const schedulerUpdatesSubscription = async () =>
          this._socketService.startListeningForSchedulerUpdates(
            channel.id,
            state.scheduleUpdateListenerHandler,
          );
        this._socketService.onHubReconnected.subscribe(() => {
          this._socketService.stopListeningForSchedulerUpdates(
            state.scheduleUpdateListenerHandler,
          );
          schedulerUpdatesSubscription();
        });
        await schedulerUpdatesSubscription();

        // Trigger initial update
        const dateTimeUtcNow = moment().utc();
        const checkPeriodEndTime = dateTimeUtcNow
          .clone()
          .add(this._currentUser.liveBroadcastingCheckPeriodMs);
        await this.triggerModeUpdateForChannel(
          channel.id,
          dateTimeUtcNow,
          checkPeriodEndTime,
        );
      }
    }
    // Note: previously this block forcibly emitted Off when every channel
    // was inactive. That overrode the natural PreLive emission from the
    // per-channel triggerModeUpdateForChannel calls above, which we now want
    // to preserve (an inactive channel with a scheduled broadcast should
    // still light up the PreLive indicator). updateGlobalBroadcastMode has
    // already been called from each triggerModeUpdateForChannel and emitted
    // the correct mode.
  }

  private createSchedulerUpdateHandler(channelId: number) {
    return async (
      updatedChannelId: number,
      startTimeUtc: Date,
      endTimeUtc: Date,
    ) => {
      if (channelId === updatedChannelId) {
        console.log(
          `ChannelBroadcastModeChecker->onSchedulerUpdated-> ${updatedChannelId}`,
          startTimeUtc,
          endTimeUtc,
        );
        this.calendarService.clearCache(false);
        const startTime = moment.utc();
        await this.triggerModeUpdateForChannel(
          updatedChannelId,
          startTime,
          startTime.clone().add(1, "day"),
        );
      }
    };
  }

  // Determine the highest priority broadcast mode across all channels
  private determineGlobalBroadcastMode(): {
    mode: EChannelBroadcastMode;
    channelId: number | null;
  } {
    let highestMode = EChannelBroadcastMode.Off;
    let nextLiveShowChannelId: number = null;
    let closestStartTime: moment.Moment = null;

    // First check if any channel is in Live mode
    for (const [channelId, state] of this.channelStates.entries()) {
      if (state.mode === EChannelBroadcastMode.Live) {
        highestMode = EChannelBroadcastMode.Live;
        return { mode: highestMode, channelId }; // Live is highest priority, return immediately with the channel ID
      }
    }

    // If no channel is Live, check for PreLive
    for (const [channelId, state] of this.channelStates.entries()) {
      if (state.mode === EChannelBroadcastMode.PreLive) {
        highestMode = EChannelBroadcastMode.PreLive;

        // Get the start time of this channel's next live show
        const startTime = state.nextLiveBroadcastPresetShow?.startTime;

        // If this is the first PreLive channel we've found or its show starts sooner than our current closest
        if (
          !closestStartTime ||
          (startTime && startTime.isBefore(closestStartTime))
        ) {
          closestStartTime = startTime;
          nextLiveShowChannelId = channelId;
        }
      }
    }

    // Update the global nextLiveBroadcastPresetShow if we have a PreLive channel
    if (nextLiveShowChannelId !== null) {
      this.nextLiveBroadcastPresetShow = this.channelStates.get(
        nextLiveShowChannelId,
      ).nextLiveBroadcastPresetShow;
      return { mode: highestMode, channelId: nextLiveShowChannelId };
    } else {
      this.nextLiveBroadcastPresetShow = null;
      return { mode: highestMode, channelId: null };
    }
  }

  // Update the global broadcast state and notify subscribers if changed
  public updateGlobalBroadcastMode() {
    let { mode: newGlobalMode, channelId } =
      this.determineGlobalBroadcastMode();

    console.log(
      `ChannelBroadcastModeChecker->Global broadcast mode changed from ${this._globalBroadcastMode} to ${newGlobalMode}`,
    );
    this._globalBroadcastMode = newGlobalMode;

    // Get channel info if we have a channel that caused this state
    const channelState =
      channelId !== null ? this.channelStates.get(channelId) : null;

    // Emit global mode update WITH channel information. We no longer downgrade
    // to Off based on isActive: an inactive channel can still legitimately be
    // in PreLive (a broadcast is scheduled but the channel hasn't been
    // activated yet) and we want the indicator to reflect that.
    this.modeUpdated.next({
      channelId: channelId,
      channelName: channelState?.channelName || null,
      mode: newGlobalMode,
      nextShow:
        this.nextLiveBroadcastPresetShow ||
        channelState?.nextLiveBroadcastPresetShow,
      isGlobalUpdate: true,
    });

    // Always emit a fresh channel-specific update for the user-selected
    // channel so the UI doesn't keep stale data after a global recalculation.
    if (this._currentChannelId) {
      this.emitChannelSpecificUpdate(this._currentChannelId);
    }
  }

  // Emit a channel-specific update
  private async emitChannelSpecificUpdate(channelId: number) {
    if (!channelId) return;

    // Make sure we have this channel in our state
    if (!this.channelStates.has(channelId)) {
      try {
        const channel = await this._channelRepository.getChannelById(channelId);
        if (channel) {
          this.channelStates.set(channelId, {
            channelId: channel.id,
            channelName: channel.name,
            mode: EChannelBroadcastMode.Off,
            isActive: channel.isActive,
            liveBroadcastPresetShowUid: null,
            nextLiveBroadcastPresetShow: null,
          });
        } else {
          this.channelStates.set(channelId, {
            channelId: undefined,
            channelName: undefined,
            mode: EChannelBroadcastMode.Off,
            isActive: false,
          }); // Channel is not active so no live broadcasting is possible
        }
      } catch (error) {
        console.error(`Error getting channel ${channelId}:`, error);
        return;
      }
    }

    const channelState = this.channelStates.get(channelId);
    this.modeUpdated.next({
      channelId: channelId,
      channelName: channelState.channelName,
      mode: channelState.mode,
      nextShow: channelState.nextLiveBroadcastPresetShow,
    });
  }

  async triggerModeUpdate(
    startTime: moment.Moment,
    endTime: moment.Moment,
  ): Promise<void> {
    console.log(
      "ChannelBroadcastModeChecker->triggerModeUpdate -> refreshing all channels",
    );

    // First refresh all channel states to ensure we have the latest data
    const currentChannelId = this._currentChannelId;
    const allChannels = Array.from(this.channelStates.entries())
      .filter(([_, state]) => state.channelId !== null)
      .map(([channelId, _]) => channelId);

    // Process current channel first (if any) to ensure user sees that update first
    if (currentChannelId) {
      await this.triggerModeUpdateForChannel(
        currentChannelId,
        startTime,
        endTime,
      );
    }

    // Then process all other channels
    for (const channelId of allChannels) {
      if (channelId !== currentChannelId) {
        await this.triggerModeUpdateForChannel(channelId, startTime, endTime);
      }
    }

    // After updating all channels, explicitly recalculate the global state
    this.updateGlobalBroadcastMode();
  }

  async triggerModeUpdateForChannel(
    channelId: number,
    startTime: moment.Moment,
    endTime: moment.Moment,
  ): Promise<void> {
    // Get or initialize the channel state
    if (!this.channelStates.has(channelId)) {
      try {
        const channel = await this._channelRepository.getChannelById(channelId);
        if (!channel) {
          console.error(`Channel ${channelId} not found!`);
          return;
        }

        this.channelStates.set(channelId, {
          channelId,
          channelName: channel.name,
          mode: EChannelBroadcastMode.Off,
          isActive: channel.isActive,
          liveBroadcastPresetShowUid: null,
          nextLiveBroadcastPresetShow: null,
        });
      } catch (error) {
        console.error(`Error retrieving channel ${channelId}:`, error);
        return;
      }
    }

    const channelState = this.channelStates.get(channelId);
    // Inactive channels can still have scheduled broadcasts (PreLive); we let
    // the normal mode detection run so the indicator can reflect that.

    // Reset state
    channelState.mode = EChannelBroadcastMode.Off;
    if (channelState.checkStatusTimeoutReference) {
      clearTimeout(channelState.checkStatusTimeoutReference);
      channelState.checkStatusTimeoutReference = null;
    }

    if (this._lastGetShowsSubject) {
      console.log(
        `ChannelBroadcastModeChecker->canceling previous get shows request for channel ${channelId}`,
      );
      this._lastGetShowsSubject.next();
      this._lastGetShowsSubject.complete();
    }

    // Create a new subject for cancelling requests
    this._lastGetShowsSubject = new Subject<void>();
    const getClosestBroadbastPresetShow = async () => {
      const foundShows = await this._showRepository.getByDates(
        channelId,
        startTime,
        endTime,
        false,
        this._lastGetShowsSubject,
        false,
      );
      console.log(
        `ChannelBroadcastModeChecker->Found ${foundShows ? foundShows.length : 0} shows for channel ${channelId}.`,
      );
      return foundShows.find(
        (s) => s instanceof BroadcastPresetShow,
      ) as BroadcastPresetShow;
    };
    // Get the channel info
    const channelInfo =
      await this._channelRepository.getChannelInfoById(channelId);

    if (
      channelInfo.liveBroadcastPresetShowUid &&
      channelInfo.liveBroadcastPresetShowUid.length > 0
    ) {
      channelState.mode = EChannelBroadcastMode.Live;
      channelState.liveBroadcastPresetShowUid =
        channelInfo.liveBroadcastPresetShowUid;

      await this.updateLiveBroadcastPresetShowUid(
        channelState,
        channelInfo.liveBroadcastPresetShowUid,
      );

      console.log(
        `ChannelBroadcastModeChecker->Channel ${channelId} is in Live state. Ending the process...`,
      );
    } else {
      channelState.liveBroadcastPresetShowUid = null;
      console.log(
        `ChannelBroadcastModeChecker->Trying to get shows for channelId: ${channelId}. from: ${startTime} to ${endTime}`,
      );

      var closestBroadcastPresetShow = await getClosestBroadbastPresetShow();
      if (closestBroadcastPresetShow) {
        const dateTimeUtcNow = moment().utc();
        const checkPeriodEndTime = dateTimeUtcNow
          .clone()
          .add(this._currentUser.liveBroadcastingCheckPeriodMs);

        if (
          dateTimeUtcNow.isBetween(
            closestBroadcastPresetShow.startTime,
            closestBroadcastPresetShow.endTime,
          )
        ) {
          channelState.mode = EChannelBroadcastMode.Live;
          channelState.liveBroadcastPresetShowUid =
            closestBroadcastPresetShow.broadcastPresetUid;
        } else if (
          closestBroadcastPresetShow.startTime.isBetween(
            dateTimeUtcNow,
            checkPeriodEndTime,
          )
        ) {
          channelState.mode = EChannelBroadcastMode.PreLive;
          channelState.nextLiveBroadcastPresetShow = closestBroadcastPresetShow;
          channelState.liveBroadcastPresetShowUid =
            closestBroadcastPresetShow.broadcastPresetUid;
          const timeoutMs = closestBroadcastPresetShow.startTime
            .clone()
            .add(1, "seconds")
            .diff(dateTimeUtcNow, "milliseconds");

          console.log(
            `ChannelBroadcastModeChecker->Channel ${channelId}: Broadcast show ${closestBroadcastPresetShow.name} is in PreLive state. Setup Timeout for next check for ${timeoutMs} milliseconds`,
          );

          channelState.checkStatusTimeoutReference = setTimeout(() => {
            this.triggerModeUpdateForChannel(
              channelId,
              closestBroadcastPresetShow.startTime.clone(),
              closestBroadcastPresetShow.startTime.clone().add(2, "seconds"),
            );
          }, timeoutMs);
        } else if (
          closestBroadcastPresetShow.startTime.isAfter(checkPeriodEndTime)
        ) {
          const nextLiveBroadcastPeriodCheckTime =
            closestBroadcastPresetShow.startTime
              .clone()
              .subtract(
                this._currentUser.liveBroadcastingCheckPeriodMs,
                "milliseconds",
              );

          const timeoutMs = nextLiveBroadcastPeriodCheckTime.diff(
            dateTimeUtcNow,
            "milliseconds",
          );

          console.log(
            `ChannelBroadcastModeChecker->Channel ${channelId}: Broadcast show ${closestBroadcastPresetShow.name} is after liveBroadcastingCheckPeriod. Setup Timeout for ${timeoutMs} milliseconds`,
          );

          channelState.checkStatusTimeoutReference = setTimeout(() => {
            this.triggerModeUpdateForChannel(
              channelId,
              nextLiveBroadcastPeriodCheckTime,
              nextLiveBroadcastPeriodCheckTime
                .clone()
                .add(
                  this._currentUser.liveBroadcastingCheckPeriodMs,
                  "milliseconds",
                )
                .add(1, "seconds"),
            );
          }, timeoutMs);
        }
      }
    }

    console.log(
      `ChannelBroadcastModeChecker->Channel ${channelId} current state: ${channelState.mode}`,
    );
    if (
      channelState.liveBroadcastPresetShowUid &&
      channelState.mode == EChannelBroadcastMode.Off
    ) {
      channelState.liveBroadcastPresetShowUid = null;
    }
    // Update the state in the map
    this.channelStates.set(channelId, channelState);

    // If this is the current channel, emit a channel-specific update
    if (this._currentChannelId === channelId) {
      this.emitChannelSpecificUpdate(channelId);
    }

    // After updating individual channel state, update the global broadcast mode
    this.updateGlobalBroadcastMode();
  }

  async updateLiveBroadcastPresetShowUid(
    channelState: ChannelBroadcastModeState,
    liveBroadcastPresetShowUid: string,
  ) {
    if (!liveBroadcastPresetShowUid) {
      return;
    }

    const matchById = (shows: Show[]) =>
      shows.find(
        (s) =>
          s instanceof BroadcastPresetShow &&
          s.id === liveBroadcastPresetShowUid,
      ) as BroadcastPresetShow | undefined;

    // 1) Try the live-broadcasting-state endpoint.
    try {
      const shows = await this._showRepository.getLiveBroadcastingState(
        channelState.channelId,
      );
      const match = matchById(shows);
      if (match) {
        channelState.liveBroadcastPresetShowUid = match.broadcastPresetUid;
        return;
      }
    } catch {
      // fall through to the next source
    }

    // 2) Fallback: query the next hour's calendar window for this channel.
    // This is the same backend route the broadcast-preset view-model uses
    // successfully to resolve the live show when the cache is empty.
    try {
      const now = moment().utc();
      const shows = await this._showRepository.getByDates(
        channelState.channelId,
        now,
        now.clone().add(1, "hour"),
      );
      const match = matchById(shows);
      if (match) {
        channelState.liveBroadcastPresetShowUid = match.broadcastPresetUid;
      }
    } catch {
      // give up silently
    }
  }

  // Legacy method for backward compatibility
  async setupChannel() {
    const currentChannel = await this._userService.getCurrentChannel(true);
    if (currentChannel) {
      this._currentChannelId = currentChannel.id;
      await this.refreshChannels();
    }
  }

  // New methods to support working with multiple channels

  // Get the broadcast mode for a specific channel
  getStateForChannel(channelId: number): ChannelBroadcastModeState {
    return this.channelStates.get(channelId) ?? null;
  }

  // Get the current global broadcast mode (highest priority across all channels)
  getGlobalBroadcastMode(): EChannelBroadcastMode {
    return this._globalBroadcastMode;
  }

  // Get next live broadcast for a specific channel
  getNextLiveBroadcastForChannel(
    channelId: number,
  ): BroadcastPresetShow | undefined {
    return this.channelStates.get(channelId)?.nextLiveBroadcastPresetShow;
  }

  // Get all channel states
  getAllChannelStates(): Array<{
    channelId: number;
    mode: EChannelBroadcastMode;
    nextShow?: BroadcastPresetShow;
  }> {
    return Array.from(this.channelStates.entries()).map(
      ([channelId, state]) => ({
        channelId,
        mode: state.mode,
        nextShow: state.nextLiveBroadcastPresetShow,
      }),
    );
  }

  // Get all channels that are in a specific broadcast mode
  getChannelsInMode(mode: EChannelBroadcastMode): number[] {
    return Array.from(this.channelStates.entries())
      .filter(([_, state]) => state.mode === mode)
      .map(([channelId, _]) => channelId);
  }

  async getLiveAssosiatedPresetUid(
    channelId?: number,
  ): Promise<string | undefined> {
    const targetChannelId = channelId ?? this._currentChannelId;
    if (!targetChannelId) {
      return undefined;
    }

    const channelState = this.channelStates.get(targetChannelId);
    if (!channelState) {
      return undefined;
    }

    // PreLive: nextLiveBroadcastPresetShow already holds the preset uid.
    if (channelState.nextLiveBroadcastPresetShow) {
      return channelState.nextLiveBroadcastPresetShow.broadcastPresetUid;
    }

    if (!channelState.liveBroadcastPresetShowUid) {
      return undefined;
    }

    // Idempotently ensure the field holds the preset uid. With the robust
    // updateLiveBroadcastPresetShowUid this succeeds for Live-from-channelInfo
    // even when the initial conversion at setup time silently failed (e.g.,
    // getLiveBroadcastingState didn't return the show — the getByDates
    // fallback inside picks it up).
    await this.updateLiveBroadcastPresetShowUid(
      channelState,
      channelState.liveBroadcastPresetShowUid,
    );

    return channelState.liveBroadcastPresetShowUid;
  }
}
